import { ValidStrengthType } from '@uikit/FormItems/PasswordStrength';
import { IMAGES } from '@image/images';
import { toast } from 'react-toastify';

type LabelItem = {
  label?: string | null;
};

type PermissionRoleType = {
  value?: string;
  label?: string;
};

export const parseLatLon = (value) => {
  const isValidLatLon = (value) => {
    const latLonRegex =
      /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|(\d{1,2}))(\.\d+)?)$/;
    return latLonRegex.test(value);
  };

  if (isValidLatLon(value)) {
    const parts = value.split(',').map((v) => parseFloat(v.trim()));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      let [latitude, longitude] = parts;

      latitude =
        latitude >= -90 && latitude <= 90
          ? parseFloat(latitude.toFixed(4))
          : null;
      longitude =
        longitude >= -180 && longitude <= 180
          ? parseFloat(longitude.toFixed(4))
          : null;

      return { latitude, longitude };
    }
  }

  return { latitude: null, longitude: null };
};

export const hasValidValues = (obj) => {
  return (
    obj &&
    Object.values(obj).every(
      (value) => value !== undefined && value !== null && value !== ''
    )
  );
};

export function getLatLngFromSuggestions(suggestions) {
  if (!Array.isArray(suggestions)) return null;

  for (const suggestion of suggestions) {
    const latitude = suggestion?.location?.lat;
    const longitude = suggestion?.location?.lng;

    if (typeof latitude === 'number' && typeof longitude === 'number') {
      return {
        latitude: Number(latitude.toFixed(5)),
        longitude: Number(longitude.toFixed(5)),
      };
    }
  }

  return null;
}

export const getTimerColor = (
  isFindingDriver: boolean,
  isDriverWaiting: boolean,
  seconds: number
) => {
  const minutes = seconds / 60;

  if (isFindingDriver) {
    if (minutes < 3) return '#32936F';
    if (minutes < 9) return '#C6470FFF';
    return '#FF3B30FF';
  }

  if (isDriverWaiting) {
    if (minutes < 3) return '#32936F';
    if (minutes < 4) return '#C6470FFF';
    return '#FF3B30FF';
  }
};

export type OnTimeStatusStyle = {
  backgroundColor: string;
  color: string;
};

export const getOnTimeStatusStyle = (
  rawValue: string
): OnTimeStatusStyle | null => {
  const str = String(rawValue).trim();
  const lower = str.toLowerCase();

  if (lower.includes('on') && lower.includes('time')) {
    return {
      backgroundColor: '#32936F',
      color: '#FFFFFFFF',
    };
  }

  if (lower.includes('late')) {
    return {
      backgroundColor: '#FF3B30FF',
      color: '#FFFFFFFF',
    };
  }

  return {
    backgroundColor: '#BABABAFF',
    color: '#292850FF',
  };
};

type UserOnlineStatus = 'Online' | 'Away' | 'Offline';

const USER_ONLINE_STATUS_MAP: Record<string, UserOnlineStatus> = {
  online: 'Online',
  away: 'Away',
  offline: 'Offline',
};

//TODO: Contractor sorting should be done in backend, not frontend.

export const sortByLabelAsc = <T extends LabelItem>(items: T[]): T[] => {
  return [...items].sort((a, b) =>
    (a?.label || '').localeCompare(b?.label || '', undefined, {
      sensitivity: 'base',
    })
  );
};

//TODO this shoot be done by backend!!!
export const sortPermissionRoleTypes = (roles: PermissionRoleType[]) => {
  const getRolePriority = (role: PermissionRoleType): number => {
    const label = (role.label ?? '').toLowerCase();
    const value = (role.value ?? '').toLowerCase();

    if (label.includes('app')) {
      return value.includes('delegate') ? 2 : 1;
    }
    return 0;
  };

  return [...roles].sort((a, b) => getRolePriority(a) - getRolePriority(b));
};

export const normalizeOnlineStatus = (
  status?: string | null
): UserOnlineStatus | undefined =>
  USER_ONLINE_STATUS_MAP[
    String(status ?? '')
      .trim()
      .toLowerCase()
  ];

export const stGroupColors = {
  unassigned: '#FFCC91FF',
  assigned: '#4F7EFFFF',
  inProgress: '#9F9FF8FF',
  canceled: '#BABABAFF',
  incomplete: '#FF3B30FF',
  complete: '#32936FFF',
};

export const BgColors = {
  GREEN: '#32936F',
  ORANGE: '#C6470FFF',
  RED: '#FF3B30FF',
  GRAY: '#BABABAFF',
  BLUE: '#57568EFF',
  NEW: '#4f7eff',
};

export const statusGroupColors = {
  VerifiedComplete: '#32936FFF',
  Incomplete: '#ED6214FF',
  Canceled: '#BABABAFF',
  Scheduled: '#4F7EFFFF',
  Unassigned: '#FFCC91FF',
  InProgress: '#9F9FF8FF',
  Rejected: '#ff3b30',
  Unknown: '#565D6DFF',
};

const TextColors = {
  WHITE: '#FFFFFFFF',
  BLACK: '#292850FF',
};

const STATUS_STYLES = {
  ASSIGNED: {
    backgroundColor: BgColors.GREEN,
    color: TextColors.WHITE,
    stColor: stGroupColors.assigned,
  },
  IN_PROGRESS: {
    backgroundColor: BgColors.BLUE,
    color: TextColors.WHITE,
    stColor: stGroupColors.inProgress,
  },
  CANCELED: {
    backgroundColor: BgColors.GRAY,
    color: TextColors.BLACK,
    stColor: stGroupColors.canceled,
  },
  INCOMPLETE: {
    backgroundColor: BgColors.GRAY,
    color: TextColors.BLACK,
    stColor: stGroupColors.incomplete,
  },
  ACTION_REQUIRED: {
    backgroundColor: BgColors.ORANGE,
    color: TextColors.WHITE,
    stColor: stGroupColors.unassigned,
  },
  COMPLETE: {
    backgroundColor: BgColors.GREEN,
    color: TextColors.WHITE,
    stColor: stGroupColors.complete,
  },
  NEW: {
    backgroundColor: BgColors.NEW,
    color: TextColors.WHITE,
    stColor: stGroupColors.assigned,
  },
  FINAL: {
    backgroundColor: BgColors.RED,
    color: TextColors.WHITE,
    stColor: stGroupColors.complete,
  },
};

export const assignHistoryColors = {
  Assigned: {
    backgroundColor: BgColors.GREEN,
    color: TextColors.WHITE,
  },
  Unassigned: {
    backgroundColor: BgColors.RED,
    color: TextColors.WHITE,
  },
  Canceled: {
    backgroundColor: BgColors.RED,
    color: TextColors.WHITE,
  },
  Reactivated: {
    backgroundColor: BgColors.GREEN,
    color: TextColors.WHITE,
  },
};

export const RIDE_STATUS_IDS = {
  SCHEDULED: 15,
  SENT_TO_RIDESHARE: 9999,
} as const;

export const isWillCallActivationStatus = (statusId?: number | null): boolean =>
  [RIDE_STATUS_IDS.SENT_TO_RIDESHARE, 30].includes(Number(statusId));

const STATUS_VALUES = {
  assignedToContractor: 'Assigned to contractor',
  assignedToVehicle: 'Assigned to vehicle',
  enRouteToPickup: 'Enroute to pickup',
  approachingPickup: 'Approaching pickup',
  arrivedAtPickup: 'Arrived at pickup',
  enRouteToDropOff: 'Enroute to drop-off',
  departedFromDropOff: 'Departed from drop-off',
  canceled: 'Canceled',
  incompleteMemberNoShow: 'Incomplete – member no show',
  incompleteDriverNoShow: 'Incomplete – driver no show',
  incompleteResearchRequired: 'Incomplete - research required',
  verifiedComplete: 'Verified complete',
  rejected: 'Rejected',
  searchingForAVehicle: 'Searching for a vehicle',
  scheduled: 'Scheduled',
  unassigned: 'Unassigned',
  canceledPassengerNoShow: 'Canceled passenger no show',
  expired: 'Expired',
  canceledNoDriverAvailable: 'Canceled no driver available',
  transmitError: 'Transmit error',
  notReceived: 'Not Received',
  unknown: 'Unknown',
  failed: 'Failed',
  canceledByDriver: 'Canceled by driver',
  canceledByDispatcher: 'Canceled by dispatcher',
  canceledByPassenger: 'Canceled by passenger',
  deactivated: 'Deactivated',
  flexRideCreated: 'Flex ride created',
  flexRideReady: 'Flex ride ready',
  reactivate: 'Reactivate',
  arrivedAtDropOff: 'Arrived at drop-off',
  new: 'New',
  finalDestination: 'Final Destination',
  driverFound: 'Driver Found',
  rideCanceled: 'Ride Canceled',
};

const addTextAfterStatus = (text: string) => {
  return `${text} for `;
};

export const allStatuses = {
  1: {
    id: 1,
    label: 'Assigned',
    value: STATUS_VALUES.assignedToContractor,
    rideShare: 'notReceived',
    ...STATUS_STYLES.ASSIGNED,
    MTableBackgroundColor: BgColors.ORANGE,
    loaderStatus: 'pending',
    group: 'Scheduled',
    groupColor: statusGroupColors.Scheduled,
  },
  2: {
    id: 2,
    label: 'Assigned',
    value: STATUS_VALUES.assignedToVehicle,
    rideShare: 'accepted',
    ...STATUS_STYLES.ASSIGNED,
    loaderStatus: 'success',
    group: 'In progress',
    groupColor: statusGroupColors.InProgress,
  },
  3: {
    id: 3,
    label: 'In progress',
    value: STATUS_VALUES.enRouteToPickup,
    rideShare: 'Accepted',
    ...STATUS_STYLES.IN_PROGRESS,
    loaderStatus: 'success',
    group: 'In progress',
    groupColor: statusGroupColors.InProgress,
  },
  4: {
    id: 4,
    label: 'In progress',
    value: STATUS_VALUES.approachingPickup,
    rideShare: 'Approaching',
    ...STATUS_STYLES.IN_PROGRESS,
    loaderStatus: 'success',
    group: 'In progress',
    groupColor: statusGroupColors.InProgress,
  },
  5: {
    id: 5,
    label: 'In progress',
    value: STATUS_VALUES.arrivedAtPickup,
    rideShare: 'arrived',
    ...STATUS_STYLES.IN_PROGRESS,
    loaderStatus: 'success',
    group: 'In progress',
    groupColor: statusGroupColors.InProgress,
  },
  6: {
    id: 6,
    label: 'In progress',
    value: STATUS_VALUES.enRouteToDropOff,
    rideShare: 'pickedUp',
    ...STATUS_STYLES.IN_PROGRESS,
    loaderStatus: 'success',
    group: 'In progress',
    groupColor: statusGroupColors.InProgress,
  },
  7: {
    id: 7,
    label: 'In progress',
    value: STATUS_VALUES.departedFromDropOff,
    rideShare: 'droppedOff',
    ...STATUS_STYLES.IN_PROGRESS,
    loaderStatus: 'success',
    group: 'In progress',
    groupColor: statusGroupColors.InProgress,
  },
  8: {
    id: 8,
    label: 'Canceled',
    value: STATUS_VALUES.canceled,
    rideShare: 'canceled',
    ...STATUS_STYLES.CANCELED,
    loaderStatus: 'success',
    group: 'Canceled',
    groupColor: statusGroupColors.Canceled,
  },
  9: {
    id: 9,
    label: 'Incomplete',
    value: STATUS_VALUES.incompleteMemberNoShow,
    rideShare: 'Incomplete',
    ...STATUS_STYLES.INCOMPLETE,
    loaderStatus: 'success',
    group: 'Incomplete',
    groupColor: statusGroupColors.Incomplete,
  },
  10: {
    id: 10,
    label: 'Incomplete',
    value: STATUS_VALUES.incompleteDriverNoShow,
    rideShare: 'Incomplete',
    ...STATUS_STYLES.INCOMPLETE,
    loaderStatus: 'success',
    group: 'Incomplete',
    groupColor: statusGroupColors.Incomplete,
  },
  11: {
    id: 11,
    label: 'Incomplete',
    value: STATUS_VALUES.incompleteResearchRequired,
    rideShare: 'Incomplete',
    ...STATUS_STYLES.INCOMPLETE,
    loaderStatus: 'success',
    group: 'Incomplete',
    groupColor: statusGroupColors.Incomplete,
  },
  12: {
    id: 12,
    label: 'Complete',
    value: STATUS_VALUES.verifiedComplete,
    rideShare: 'completed',
    ...STATUS_STYLES.COMPLETE,
    loaderStatus: 'success',
    group: 'Completed',
    groupColor: statusGroupColors.VerifiedComplete,
  },
  13: {
    id: 13,
    label: 'Rejected',
    value: STATUS_VALUES.rejected,
    rideShare: 'rejected',
    backgroundColor: BgColors.RED,
    color: TextColors.WHITE,
    stColor: statusGroupColors.Rejected,
    loaderStatus: 'error',
    group: 'Rejected',
    groupColor: statusGroupColors.Rejected,
  },
  14: {
    id: 14,
    label: 'Pending',
    value: STATUS_VALUES.searchingForAVehicle,
    rideShare: 'pending',
    ...STATUS_STYLES.IN_PROGRESS,
    loaderStatus: 'success',
    group: 'In progress',
    groupColor: statusGroupColors.InProgress,
  },
  15: {
    id: 15,
    label: 'Scheduled',
    value: STATUS_VALUES.scheduled,
    rideShare: 'Scheduled',
    ...STATUS_STYLES.ASSIGNED,
    loaderStatus: 'success',
    group: 'Scheduled',
    groupColor: statusGroupColors.Scheduled,
  },
  16: {
    id: 16,
    label: 'Unassigned',
    value: STATUS_VALUES.unassigned,
    rideShare: 'notAssigned',
    backgroundColor: '#32936F',
    color: '#FFFFFFFF',
    stColor: statusGroupColors.Unassigned,
    loaderStatus: 'pending',
    group: 'Unassigned',
    groupColor: statusGroupColors.Unassigned,
  },
  18: {
    id: 18,
    label: 'No show',
    value: STATUS_VALUES.canceledPassengerNoShow,
    rideShare: 'canceled_passenger_no_show',
    ...STATUS_STYLES.INCOMPLETE,
    loaderStatus: 'success',
    group: 'Incomplete',
    groupColor: statusGroupColors.Incomplete,
  },
  19: {
    id: 19,
    label: 'No show',
    value: STATUS_VALUES.expired,
    rideShare: 'expired',
    ...STATUS_STYLES.CANCELED,
    loaderStatus: 'success',
    group: 'Incomplete',
    groupColor: statusGroupColors.Incomplete,
  },
  20: {
    id: 20,
    label: 'Action required',
    value: STATUS_VALUES.canceledNoDriverAvailable,
    rideShare: 'canceled_no_driver_available',
    ...STATUS_STYLES.CANCELED,
    loaderStatus: 'success',
    group: 'Canceled',
    groupColor: statusGroupColors.Canceled,
  },
  21: {
    id: 21,
    label: 'Action required',
    value: STATUS_VALUES.transmitError,
    rideShare: 'transmitError',
    backgroundColor: '#C6470FFF',
    color: '#ffffff',
    stColor: stGroupColors.canceled,
    loaderStatus: 'error',
    group: 'Unassigned',
    groupColor: statusGroupColors.Unassigned,
  },
  22: {
    id: 22,
    label: 'Action required',
    value: STATUS_VALUES.notReceived,
    rideShare: 'notReceived',
    ...STATUS_STYLES.ACTION_REQUIRED,
    loaderStatus: 'error',
    group: 'Unassigned',
    groupColor: statusGroupColors.Unassigned,
  },
  23: {
    id: 23,
    label: 'Action required',
    value: STATUS_VALUES.unknown,
    rideShare: 'notReceived',
    ...STATUS_STYLES.ACTION_REQUIRED,
    loaderStatus: 'error',
    group: 'Unassigned',
    groupColor: statusGroupColors.Unassigned,
  },
  24: {
    id: 24,
    label: 'Action required',
    value: STATUS_VALUES.failed,
    rideShare: 'failed',
    ...STATUS_STYLES.ACTION_REQUIRED,
    loaderStatus: 'error',
    group: 'Unassigned',
    groupColor: statusGroupColors.Unassigned,
  },
  25: {
    id: 25,
    label: 'Canceled',
    value: STATUS_VALUES.canceledByDriver,
    rideShare: 'canceled_by_driver',
    ...STATUS_STYLES.CANCELED,
    loaderStatus: 'success',
    group: 'Canceled',
    groupColor: statusGroupColors.Canceled,
  },
  26: {
    id: 26,
    label: 'Canceled',
    value: STATUS_VALUES.canceledByDispatcher,
    rideShare: 'canceled_by_dispatcher',
    ...STATUS_STYLES.CANCELED,
    loaderStatus: 'success',
    group: 'Canceled',
    groupColor: statusGroupColors.Canceled,
  },
  27: {
    id: 27,
    label: 'Canceled',
    value: STATUS_VALUES.canceledByPassenger,
    rideShare: 'canceled_by_passenger',
    ...STATUS_STYLES.CANCELED,
    loaderStatus: 'success',
    group: 'Canceled',
    groupColor: statusGroupColors.Canceled,
  },
  28: {
    id: 28,
    label: 'Canceled',
    value: STATUS_VALUES.deactivated,
    rideShare: 'deactivated',
    ...STATUS_STYLES.CANCELED,
    loaderStatus: 'success',
    group: 'Canceled',
    groupColor: statusGroupColors.Canceled,
  },
  29: {
    id: 29,
    label: 'Created',
    value: STATUS_VALUES.flexRideCreated,
    rideShare: 'created',
    ...STATUS_STYLES.ASSIGNED,
    loaderStatus: 'success',
    group: 'Scheduled',
    groupColor: statusGroupColors.Scheduled,
  },
  30: {
    id: 30,
    label: 'Ready',
    value: STATUS_VALUES.flexRideReady,
    rideShare: 'ready',
    ...STATUS_STYLES.ASSIGNED,
    loaderStatus: 'success',
    group: 'Scheduled',
    groupColor: statusGroupColors.Scheduled,
  },
  31: {
    id: 31,
    label: 'Reactivate',
    value: STATUS_VALUES.reactivate,
    rideShare: 'ready',
    ...STATUS_STYLES.ASSIGNED,
    loaderStatus: 'success',
    group: 'Unassigned',
    groupColor: statusGroupColors.Unassigned,
  },
  32: {
    id: 32,
    label: 'In progress',
    value: STATUS_VALUES.arrivedAtDropOff,
    rideShare: 'arrivedDroppedOff',
    ...STATUS_STYLES.IN_PROGRESS,
    loaderStatus: 'success',
    group: 'In progress',
    groupColor: statusGroupColors.InProgress,
  },
  100: {
    id: 100,
    label: 'New',
    value: STATUS_VALUES.new,
    rideShare: null,
    ...STATUS_STYLES.NEW,
    loaderStatus: 'pending',
    group: 'Unassigned',
    groupColor: statusGroupColors.Unassigned,
  },
  101: {
    id: 101,
    label: 'final',
    value: STATUS_VALUES.finalDestination,
    rideShare: null,
    ...STATUS_STYLES.FINAL,
    loaderStatus: 'pending',
    group: 'Unassigned',
    groupColor: statusGroupColors.Unassigned,
  },
  9999: {
    id: 9999,
    label: 'Assigned',
    value: STATUS_VALUES.assignedToContractor,
    rideShare: 'notReceived',
    ...STATUS_STYLES.ASSIGNED,
    MTableBackgroundColor: BgColors.ORANGE,
    loaderStatus: 'success',
    group: 'In progress',
    groupColor: statusGroupColors.InProgress,
  },
};

export const findingDriver = 'Finding a driver...';
export const driverWaiting = 'Driver waiting...';

export const findingDriverStatusIds = [14];

export const driverWaitingStatusIds = [5];

export const getStatusIds = {
  showAll: [],
  inProgress: [2, 3, 4, 5, 6, 7, 14, 32],
  riderNoShow: [18, 19],
  actionRequired: [1, 20, 21, 22, 23, 24, 9999],
  canceled: [8, 25, 26, 27, 28, 13],
  isCompleted: [7, 8, 9, 10, 11, 12, 13, 18, 19, 20, 25, 26, 27, 28],
  isCompletedFromReactivate: [12],
  willCallIsActive: [30],
};

export const dontDisabledStatusIds = [15, 29, 30, 9999];

export const FINALIZED_STATUS_IDS = new Set<number>([
  3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 15, 18, 19, 20, 21, 22, 23, 24, 25, 26,
  27, 28, 30, 32,
]);

export const NON_RIDESHARE_FINALIZED_STATUS_IDS = new Set([
  ...FINALIZED_STATUS_IDS,
  ...[1, 9999],
]);

export const isValidJson = (str: string | null): boolean => {
  if (typeof str !== 'string') return false;
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
};

export const configLOB = {
  MCD: 'Medicaid',
  MCR: 'Medicare',
  COM: 'Commercial',
  DUAL: 'Dual Membership',
};

const order = ['MCR', 'MCD', 'COM'];

export const switcherConfigsLOB = {
  MCD: `${configLOB.MCD} Limits`,
  MCR: `${configLOB.MCR} Limits`,
  COM: `${configLOB.COM} Limits`,
};

export const sortedLobSwitcher = (data: any) =>
  [...data].sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value));

export const removeEmpty = (obj: any): any => {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([, v]) =>
        !(typeof v === 'object' && v !== null && Object.keys(v).length === 0)
    )
  );
};

export const evaluatePasswordStrength = (
  password: string
): ValidStrengthType => {
  if (password.length < 8) {
    return 'Poor';
  }

  const hasLower = /[a-z]/.test(password);
  const hasUpper = /[A-Z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);
  const longEnough = password.length >= 12;

  if (hasLower && hasUpper && hasNumber && hasSpecial && longEnough) {
    return 'Excellent';
  }

  const score =
    Number(hasLower) +
    Number(hasUpper) +
    Number(hasNumber) +
    Number(hasSpecial);

  if (score <= 1) return 'Poor';
  if (score === 2) return 'Weak';
  if (score >= 3) return 'Strong';

  return 'Poor';
};

export const getRideStatusState = ({ watch, index }) => {
  const statusId = watch('ridesSegments')?.[index]?.status?.id;
  const isActiveWillCall =
    watch('ridesSegments')?.[index]?.callIndicator &&
    watch('ridesSegments')?.[index]?.scheduledPickupTime;
  const isWillCallDispatched = isActiveWillCall ? [30] : [];
  // TODO check ths logic properly
  const permanentDisable = watch('ridesSegments')?.some((el) =>
    FINALIZED_STATUS_IDS?.has(el?.status?.id)
  );
  return {
    isScheduled: [15, 29, 30, 9999].includes(statusId),
    isDispatched: [2, 3, 4, 5, 6, 14, 32, ...isWillCallDispatched].includes(
      statusId
    ),
    isCompleted: [7, 8, 9, 10, 11, 12, 13, 18, 19, 20, 25, 26, 27, 28].includes(
      statusId
    ),
    permanentDisable,
  };
};

export const hasParams = (queryParams: Record<string, any>) =>
  Object.values(queryParams).some((v) => v != null && v !== '');

export const notificationConfigurations = (image?: string) => ({
  1: {
    title: '',
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  2: {
    title: addTextAfterStatus(STATUS_VALUES.driverFound),
    image,
    isButtonHidden: false,
  },
  3: {
    title: addTextAfterStatus(STATUS_VALUES.driverFound),
    image,
    isButtonHidden: false,
  },
  4: {
    title: addTextAfterStatus(STATUS_VALUES.approachingPickup),
    image,
    isButtonHidden: false,
  },
  5: {
    title: addTextAfterStatus(STATUS_VALUES.arrivedAtPickup),
    image,
    isButtonHidden: false,
  },
  6: {
    title: addTextAfterStatus(STATUS_VALUES.enRouteToDropOff),
    image,
    isButtonHidden: false,
  },
  7: {
    title: addTextAfterStatus(STATUS_VALUES.departedFromDropOff),
    image,
    isButtonHidden: false,
  },
  8: {
    title: addTextAfterStatus(STATUS_VALUES.rideCanceled),
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  9: {
    title: addTextAfterStatus(STATUS_VALUES.rideCanceled),
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  10: {
    title: addTextAfterStatus(STATUS_VALUES.rideCanceled),
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  11: {
    title: addTextAfterStatus(STATUS_VALUES.rideCanceled),
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  12: {
    title: 'Ride is completed for ',
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  13: {
    title: 'Ride Rejected for ',
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  14: {
    title: addTextAfterStatus(STATUS_VALUES.searchingForAVehicle),
    image: IMAGES.carPlaceholder,
    isButtonHidden: false,
  },
  15: {
    title: addTextAfterStatus(STATUS_VALUES.scheduled),
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  16: {
    title: addTextAfterStatus(STATUS_VALUES.unassigned),
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  18: {
    title: addTextAfterStatus(STATUS_VALUES.rideCanceled),
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  19: {
    title: addTextAfterStatus(STATUS_VALUES.rideCanceled),
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  20: {
    title: 'No Driver Found for ',
    image: IMAGES.noDriverFound,
    isButtonHidden: true,
  },
  21: {
    title: addTextAfterStatus(STATUS_VALUES.transmitError),
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  22: {
    title: addTextAfterStatus(STATUS_VALUES.notReceived),
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  23: {
    title: addTextAfterStatus(STATUS_VALUES.unknown),
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  24: {
    title: 'Failed for ',
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  25: {
    title: addTextAfterStatus(STATUS_VALUES.rideCanceled),
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  26: {
    title: addTextAfterStatus(STATUS_VALUES.rideCanceled),
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  27: {
    title: addTextAfterStatus(STATUS_VALUES.rideCanceled),
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  28: {
    title: 'Ride Deactivated for ',
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  29: {
    title: addTextAfterStatus(STATUS_VALUES.flexRideCreated),
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  30: {
    title: addTextAfterStatus(STATUS_VALUES.flexRideReady),
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  31: {
    title: addTextAfterStatus(STATUS_VALUES.reactivate),
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  32: {
    title: addTextAfterStatus(STATUS_VALUES.arrivedAtDropOff),
    image,
    isButtonHidden: false,
  },
  100: {
    title: addTextAfterStatus(STATUS_VALUES.new),
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  101: {
    title: addTextAfterStatus(STATUS_VALUES.finalDestination),
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
  9999: {
    title: 'Ride sent to rideshare company for ',
    image: IMAGES.defaultImage,
    isButtonHidden: false,
  },
});

export const formatPickupTime = (time?: string) => {
  if (!time) return '';
  if (!time.includes(':')) {
    return time;
  }

  const [hours, minutes] = time.split(':');
  return `${hours}:${minutes}`;
};

export const statusDotByField: Record<string, string> = {
  driverAccepted: '#4F7EFFFF',
  pickUpArrive: '#3CB085FF',
  pickUpDepart: '#28765AFF',
  dropOffArrive: '#E85211FF',
  dropOffDepart: '#A1390CFF',
};

export const setDefaultStorageItems = (key: string) => {
  if (
    typeof window !== 'undefined' &&
    window.localStorage.getItem(key) === null
  ) {
    window.localStorage.setItem(key, JSON.stringify(false));
  }
};

export const extractBooleanValue = (value: string | null) => {
  return value ? JSON.parse(value) : false;
};

interface ApiError {
  data?: {
    details?: string[];
    message?: string;
  };
  message?: string;
}

const getErrorMessages = (error: unknown): string[] => {
  const err = error as ApiError;
  const details = err?.data?.details;

  if (Array.isArray(details) && details.length > 0) return details;

  const fallback = err?.data?.message || err?.message;
  return fallback ? [fallback] : ['Something went wrong'];
};

export const toastError = (error: unknown, fallbackMessage = '') => {
  const err = error as ApiError;
  const messages = getErrorMessages(err);
  const text =
    messages.length > 1
      ? messages.map((m) => `• ${m}`).join('\n')
      : messages[0] || fallbackMessage;

  toast.error(text);
};
